import apiClient from '@/lib/api-client'
import type { TeamMember } from '@/shared/types/entities'
import { WorkspaceRole } from '@/shared/types/entities'

export interface CreateClientDto {
  name: string
}

export interface CreateProjectDto {
  name: string
  clientId?: string
  color: string
  isBillable: boolean
  hourlyRate?: number
}

export interface CreateTaskDto {
  name: string
  projectId: string
}

export interface InviteMemberDto {
  email: string
  role: WorkspaceRole
}

export const adminService = {
  // Workspace Members
  async getWorkspaceMembers(workspaceId: string): Promise<TeamMember[]> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/members`)
    return response.data.data
  },

  async inviteMember(workspaceId: string, data: InviteMemberDto): Promise<void> {
    await apiClient.post(`/workspaces/${workspaceId}/members/invite`, data)
  },

  async updateMemberRole(
    workspaceId: string,
    userId: string,
    role: WorkspaceRole
  ): Promise<void> {
    await apiClient.patch(`/workspaces/${workspaceId}/members/${userId}`, { role })
  },

  async removeMember(workspaceId: string, userId: string): Promise<void> {
    await apiClient.delete(`/workspaces/${workspaceId}/members/${userId}`)
  },

  // Entity Management - Clients
  async getClients(workspaceId: string): Promise<Array<{ id: string; name: string }>> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/clients`)
    return response.data
  },

  async createClient(workspaceId: string, data: CreateClientDto): Promise<void> {
    await apiClient.post(`/workspaces/${workspaceId}/clients`, data)
  },

  async updateClient(workspaceId: string, clientId: string, data: CreateClientDto): Promise<void> {
    await apiClient.patch(`/workspaces/${workspaceId}/clients/${clientId}`, data)
  },

  async archiveClient(workspaceId: string, clientId: string): Promise<void> {
    await apiClient.patch(`/workspaces/${workspaceId}/clients/${clientId}/archive`)
  },

  // Entity Management - Projects
  async getProjects(workspaceId: string): Promise<Array<{ id: string; name: string; clientId?: string; color: string; isArchived: boolean }>> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/projects`)
    return response.data
  },

  async createProject(workspaceId: string, data: CreateProjectDto): Promise<void> {
    await apiClient.post(`/workspaces/${workspaceId}/projects`, data)
  },

  async updateProject(workspaceId: string, projectId: string, data: Partial<CreateProjectDto>): Promise<void> {
    await apiClient.patch(`/workspaces/${workspaceId}/projects/${projectId}`, data)
  },

  async archiveProject(workspaceId: string, projectId: string): Promise<void> {
    await apiClient.patch(`/workspaces/${workspaceId}/projects/${projectId}/archive`)
  },

  // Entity Management - Tasks
  async getTasks(workspaceId: string, projectId: string): Promise<Array<{ id: string; name: string; isArchived: boolean }>> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/projects/${projectId}/tasks`)
    return response.data
  },

  async createTask(workspaceId: string, data: CreateTaskDto): Promise<void> {
    await apiClient.post(`/workspaces/${workspaceId}/tasks`, data)
  },

  async updateTask(workspaceId: string, taskId: string, data: Partial<CreateTaskDto>): Promise<void> {
    await apiClient.patch(`/workspaces/${workspaceId}/tasks/${taskId}`, data)
  },

  async archiveTask(workspaceId: string, taskId: string): Promise<void> {
    await apiClient.patch(`/workspaces/${workspaceId}/tasks/${taskId}/archive`)
  },

  // Workspace Usage Stats
  async getWorkspaceUsageStats(workspaceId: string): Promise<{
    activeUsers: number
    totalTimeTrackedSeconds: number
    apiCallsThisMonth: number
    storageUsedBytes: number
    projectCount: number
    taskCount: number
    entryCount: number
  }> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/stats`)
    return response.data
  },
}

export default adminService
